import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { CreateTrayData, GetTrayEntriesList, UpdateTrayData } from 'api/tray';
import {
  PaginationData,
  Tray,
  TrayInventoryEntry,
} from 'constants/interface';
import { RootState } from 'state';

const trayEntityAdapter = createEntityAdapter<Tray>();
const entriesEntityAdapter = createEntityAdapter<TrayInventoryEntry>();

export interface TrayEntriesState extends EntityState<TrayInventoryEntry> {
  isLoading: boolean
  remoteTotal: number
}

export interface TraysState extends EntityState<Tray> {
  isLoading: boolean
  remoteTotal: number
  entries: TrayEntriesState
}

export const initialState: TraysState = trayEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
  entries: entriesEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
  }),
});

export const createTrayAction = createAsyncThunk(
  'trays/create',
  async (payload: CreateTrayData) => {
    const trayData = await apiClient.tray.createTray(payload);
    return trayData;
  },
);

export const updateTrayAction = createAsyncThunk(
  'trays/update',
  async (payload: UpdateTrayData) => {
    const trayData = await apiClient.tray.updateTray(payload);
    return trayData;
  },
);

export const getTrayAction = createAsyncThunk(
  'trays/get',
  async (payload: {
    trayId: string,
  }) => {
    const { trayId } = payload;
    const response = await apiClient.tray.getTray(trayId);

    return response;
  },
);

export const getTrayListAction = createAsyncThunk(
  'trays/list',
  async (payload: PaginationData) => {
    const response = await apiClient
      .tray
      .getTrayList(payload);

    return response;
  },
);

// Entries
export const getTrayEntriesListAction = createAsyncThunk(
  'trays/entries/list',
  async (payload: GetTrayEntriesList) => {
    const response = await apiClient
      .tray
      .getTrayEntriesList(payload);

    return response;
  },
);

export const traysSlice = createSlice({
  name: 'trays',
  initialState,
  reducers: {
    cleanTraysAction: trayEntityAdapter.removeAll,
    cleanTrayEntriesAction: (state) => {
      entriesEntityAdapter.removeAll(state.entries);
    },
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getTrayListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getTrayListAction.fulfilled, (state, action) => {
      trayEntityAdapter.removeAll(state);
      trayEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getTrayListAction.rejected, (state) => {
      state.isLoading = false;
    });

    // Entries
    builder.addCase(getTrayEntriesListAction.pending, (state) => {
      state.entries.isLoading = true;
    });
    builder.addCase(getTrayEntriesListAction.fulfilled, (state, action) => {
      entriesEntityAdapter.removeAll(state.entries);
      entriesEntityAdapter.addMany(state.entries, action.payload.data);
      state.entries.remoteTotal = action.payload.pagination.total;
      state.entries.isLoading = false;
    });
    builder.addCase(getTrayEntriesListAction.rejected, (state) => {
      state.entries.isLoading = false;
    });
  },
});

export const {
  cleanTraysAction,
  cleanTrayEntriesAction,
} = traysSlice.actions;

// Selectors

export const isLoadingTraysSelector = (state: RootState):
  boolean => state.trays.isLoading;

export const traysEntitiesSelector = (state: RootState):
  Dictionary<Tray> => state.trays.entities;

export const traysIdsSelector = (state: RootState):
  EntityId[] => state.trays.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.trays.remoteTotal;

// Entries selectors

export const isLoadingTrayEntriesSelector = (state: RootState):
  boolean => state.trays.entries.isLoading;

export const trayEntriesEntitiesSelector = (state: RootState):
  Dictionary<TrayInventoryEntry> => state.trays.entries.entities;

export const trayEntriesIdsSelector = (state: RootState):
  EntityId[] => state.trays.entries.ids;

export const trayEntriesRemoteTotalSelector = (state: RootState):
  number => state.trays.entries.remoteTotal;
